import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SseService {
  private eventSource: EventSource | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;
  private reconnectTimeoutId?: any;
  private currentUseAuth: boolean = true;

  constructor(private zone: NgZone) {}

  /**
   * Conecta ao endpoint SSE
   * @param url URL do endpoint SSE (ex: https://api.fueltech.cloud/telemetry/stream/{carId})
   * @param eventName Nome do evento SSE (opcional, padrão é 'message')
   * @param useAuth Se deve usar autenticação (padrão: true)
   * @returns Observable com os dados recebidos
   */
  connect<T>(url: string, eventName: string = 'message', useAuth: boolean = true): Observable<T> {
    // Salva configuração de autenticação para uso na reconexão
    this.currentUseAuth = useAuth;
    
    return new Observable(observer => {
      // Adiciona token de autenticação se solicitado
      let urlWithAuth = url;
      if (useAuth) {
        const token = this.getAuthToken();
        const separator = url.includes('?') ? '&' : '?';
        urlWithAuth = token ? `${url}${separator}token=${token}` : url;
      }

      // Fecha conexão anterior se existir
      if (this.eventSource) {
        this.eventSource.close();
      }

      try {
        this.eventSource = new EventSource(urlWithAuth);
        
      } catch (error) {
        observer.error(`Failed to create EventSource: ${error}`);
        return;
      }

      // Evento de abertura da conexão
      this.eventSource.onopen = () => {
        this.zone.run(() => {

          this.reconnectAttempts = 0;
          
          // Limpa timeout de reconexão se houver
          if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
            this.reconnectTimeoutId = undefined;
          }
        });
      };

      // Handler para processar eventos SSE (evita duplicação)
      const handleEvent = (event: MessageEvent) => {
        this.zone.run(() => {
          try {
            const data = JSON.parse(event.data);
            observer.next(data);
          } catch (error) {
            // Envia dados brutos se não conseguir fazer parse
            observer.next(event.data as any);
          }
        });
      };

      // Listener para evento específico solicitado
      if (eventName === 'message') {
        // Se for 'message', usa onmessage padrão
        this.eventSource.onmessage = handleEvent;
      } else {
        // Se for evento nomeado, adiciona listener específico
        this.eventSource.addEventListener(eventName, handleEvent);
        
        // Também escuta 'message' como fallback caso o servidor envie sem event type
        this.eventSource.onmessage = handleEvent;
      }

      // Evento de erro
      this.eventSource.onerror = (error) => {
        this.zone.run(() => {
          
          if (this.eventSource?.readyState === EventSource.CLOSED) {
            this.attemptReconnect(url, eventName, observer);
          } else if (this.eventSource?.readyState === EventSource.CONNECTING) {
          }
        });
      };

      // Cleanup ao desinscrever
      return () => {
        this.disconnect();
      };
    });
  }

  /**
   * Tenta reconectar ao servidor SSE
   */
  private attemptReconnect<T>(url: string, eventName: string, observer: any): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      observer.error(`Max reconnection attempts (${this.maxReconnectAttempts}) reached`);
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    
    
    this.reconnectTimeoutId = setTimeout(() => {
      // Salva a configuração de autenticação antes do disconnect (que reseta o valor)
      const useAuth = this.currentUseAuth;
      this.disconnect();
      // Usa a configuração de autenticação salva na conexão original
      this.connect<T>(url, eventName, useAuth).subscribe(observer);
    }, delay);
  }

  /**
   * Desconecta do servidor SSE
   */
  disconnect(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = undefined;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    
    this.reconnectAttempts = 0;
    this.currentUseAuth = true;
  }

  /**
   * Verifica se há uma conexão ativa
   */
  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }

  /**
   * Obtém o estado atual da conexão
   */
  getReadyState(): number {
    return this.eventSource?.readyState ?? EventSource.CLOSED;
  }

  /**
   * Obtém token de autenticação do sessionStorage
   */
  private getAuthToken(): string {
    return sessionStorage.getItem('token') || '';
  }
}
